import React from 'react'
import * as modalActions from 'actions/modalActions'
import RefreshAlertModal from 'GeneralUI/RefreshAlertModal/RefreshAlertModal.jsx'
import {
  getAuthCodeToken as apiGetAuthCodeToken,
  tokenRefresh as apiTokenRefresh
} from 'Helper/Auth/oauth-api'
import {
  logout as apiLogout
} from 'Helper/Auth/summit-auth-api'
import * as authActions from 'actions/authActions'
import debug from 'Helper/debugHelpers'
import {
  put,
  select,
  fork,
  cancel,
  call,
  cancelled
} from 'redux-saga/effects'
import { delay } from 'Helper/customReduxEffects.js'

export function * getAuthCodeToken(action) {
  try {
    debug.log('Exchanging auth code for api token.', debug.logLevels.AUTH)
    const result = yield call(apiGetAuthCodeToken, action.query, action.subProvider, action.authProvider, action.errorHandler)
    yield call(handleAuthCodeTokenResult, result, action.successFilter)
  } catch (error) {
    // some error
    debug.log('error: ' + error, debug.logLevels.AUTH, 'error')
    if (action.errorHandler != null) {
      action.errorHandler(error.message)
    }
    return null
  }
}

function * handleAuthCodeTokenResult(result, successFilter = () => true, rejectedTokenCallback = () => null) {
  if (!result) {
    debug.log('Auth code call terminated early. Resetting', debug.logLevels.AUTH)
    yield put({ type: 'xtract-auth@auth-call-end' })
  } else if (result.status !== 200) {
    debug.logObject('Error getting auth token.', result, debug.logLevels.AUTH, 'error')
  } else {
    if(successFilter(result)) {
      // if there was an access token it was a good login
      debug.log('good login - setting token', debug.logLevels.AUTH)
      // start a timer to ensure the token will be refreshed regularly
      const refreshTimer = yield fork(tokenRefreshManager)
      // issue action to store the token, refresh timer and other details
      const {
        access_token: accessToken,
        refresh_token: refreshToken
      } = result.data
      yield put(
        authActions.setToken({
          token: accessToken,
          refreshTimer,
          refreshToken: refreshToken
        })
      )
      // wait for the token to become available in the redux store before making the
      // api call to /me
      let auth
      while (true) {
        auth = yield select(state => state.auth)
        if (auth.token) {
          // now fetch the user data.
          yield put({
            type: 'XTRACT_API_CALL',
            resource: 'me',
            method: 'read',
            obj: null,
            id: null
          })
          break
        } else {
          yield delay(250)
        }
      }
    } else {
      // failed the custom success filter (for example, during ema/launch we look for the currently authorized user to be an ema user... in this case it wasn't)
      rejectedTokenCallback()
    }
  }
}

export function * tokenRefresher(config, action) {
  const { logoutCallback = () => null } = config
  let result
  const { action: repeatAction = null } = action
  let auth = yield select(state => state.auth)
  if (auth.refreshInProgress && action.action != null) {
    const { method, resource } = repeatAction
    debug.log(
      `waiting for refresh to complete before re-dispatching ${method} to ${resource}.`,
      debug.logLevels.AUTH
    )
    // if there is already a refresh in progress, don't start another
    // just wait until it finish and then retry the call
    while (true) {
      yield delay(250)
      auth = yield select(state => state.auth)
      if (!auth.refreshInProgress) {
        debug.log(
          `refresh complete - re-dispatching ${method} to ${resource}`,
          debug.logLevels.AUTH
        )
        // refresh finished so resubmit the call and exit
        yield put(action.action)
        break
      }
    }
    return
  } else if (auth.refreshInProgress) {
    debug.log(
      'attempted refresh (no action) with refresh in progress... ignoring.',
      debug.logLevels.AUTH
    )
    // this means the action is a token refresh with no associated
    // action... we don't have to worry about the associated action
    // getting lost and there is already a refresh happening so just
    // exit
    return
  }

  try {
    yield put(authActions.startTokenRefresh())
    // fork a process that will show a slow refresh alert if it isn't canceled
    // within 5 seconds
    const slowRefreshCheck = yield fork(slowCallback)

    if (auth.refreshToken != null) {
      // then start the refresh
      result = yield call(apiTokenRefresh, auth.refreshToken)
    } else {
      throw new Error('No refresh token available. Logging out.')
    }

    // then cancel the alert fork... if its been less than 5 seconds the popup
    // wont have been shown, if its been longer, cancelling wont hurt
    yield cancel(slowRefreshCheck)
    yield call(handleRefreshResponse, result, action, auth, logoutCallback)
  } catch (error) {
    // this likely only happens when there is some network error or something
    // out of our control... it does not cover a valid response from the
    // api that has a status code attached (even if not 200)
    // some error
    debug.log('token refresh error: ' + error, debug.logLevels.AUTH, 'error')

    yield put(authActions.cancelTokenRefresh())
    // preform final logout actions provided
    debug.log('calling logoutCallback', debug.logLevels.AUTH)
    yield call(logoutCallback)
    yield put({
      type: 'xtract-auth@logout'
    })
    return
  } finally {
    if (yield cancelled()) {
      debug.log('token refresh cancelled', debug.logLevels.AUTH, 'error')
    }
  }
}

function * handleRefreshResponse(result, action, auth, logoutCallback) {
  if (
    result.status === 200 &&
    result.data.message !== 'The refresh token is invalid.'
  ) {
    // if there was an access token it was a good login
    debug.log('good refresh - setting token', debug.logLevels.AUTH)
    let refreshTimer = auth.refreshTimer
    if (refreshTimer == null) {
      refreshTimer = yield fork(tokenRefreshManager)
    }
    yield put(
      {
        type: 'xtract-auth@end-token-refresh',
        refreshTimer,
        token: result.data.access_token,
        refreshToken: result.data.refresh_token
      }
    )
    if (action.action != null) {
      debug.log(
        're-dispatching action after token refresh.',
        debug.logLevels.AUTH
      )
      // now resubmit the previous action
      yield put(action.action)
    }
    return result.data.accessToken
  } else if (result.status === 503) {
    debug.log(
      'Server responded 503 unavailable, try again in 1second.',
      debug.logLevels.AUTH
    )
    // api is likely off line so we need to delay and then resubmit the
    // refresh api call.
    yield delay(1000)
    // currently the refresh in progress flag is set, so we need to clear it
    // in order to allow the next retry to happen, for now we will reuse
    // the current tokens since we don't have new replacements yet.
    yield put(
      // this action will clear the refreshInProgress flag
      {
        type: 'xtract-auth@end-token-refresh',
        refreshTimer: null, // we wont start a new manager yet
        token: auth.token,
        refreshToken: auth.refreshToken
      }
    )
    // now we can repeat the same refresh action
    yield put({
      type: 'xtract-auth@auth-token-refresh',
      action: action.action
    })
  } else {
    debug.log(
      'Token refresh failed. Bad API result:' + JSON.stringify(result),
      debug.logLevels.AUTH,
      'error'
    )
    // cancel the refresh so we can logout
    yield put({
      type: 'xtract-auth@cancel-token-refresh'
    })
    // store the current browser location for after the user logs in again
    yield put({
      type: 'xtract-auth@logout'
    })
    yield call(apiLogout)
    yield call(logoutCallback)
  }
}

function * tokenRefreshManager() {
  try {
    while (true) {
      yield delay(1000 * 60 * 4) // api expires in 5
      debug.log(
        'tokenRefreshManager(timer) initiated refresh.',
        debug.logLevels.AUTH
      )
      yield put(authActions.doTokenRefresh())
    }
  } catch ($e) {}
}

function * slowCallback() {
  yield delay(5000)
  yield slowRefreshAlert
}

export function * slowRefreshAlert() {
  yield delay(5000)
  yield put(
    modalActions.openModal('tip_modal', {
      title: 'Internet connection problem.',
      content: <RefreshAlertModal />,
      closeable: true
    })
  )
}

export function * cancelTokenRefresh() {
  const auth = yield select(state => state.auth)
  if (auth.refresherHandles.length > 0) {
    auth.refresherHandles.forEach(task => {
      if (task.isRunning && task.isRunning()) {
        cancel(task)
      }
    })
    yield put(authActions.clearRefresherHandles())
  }
  cancel(auth.refreshTimer)
  return null
}

export function * tokenRefresh() {
  yield put(
    authActions.storeRefresherHandle(yield fork(tokenRefresher, ...arguments))
  )
}
